import { takeLatest, put, call } from 'redux-saga/effects';
import i18n, { I18N_NAMESPACE } from 'i18n';

import {
  getEcommerceOrders,
  setEcommerceOrders,
  getEcommerceCurrentOrder,
  setEcommerceCurrentOrder,
  getEcommerceSubscriptions,
  setEcommerceSubscriptions,
  getEcommerceCurrentSubscription,
  setEcommerceCurrentSubscription,
  getOrderReceipt,
  actionFail,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  setSubscriptionRenewalDate,
  setLoadingActionKey
} from './ecommerce.redux';
import EcommerceService from 'shared/services/EcommerceService';
import { TAction } from 'shared/types';
import { IEcommerceOrder } from 'shared/interfaces/ecommerceOrder.interface';
import { IEcommerceSubscription } from 'shared/interfaces/ecommerceSubscription.interface';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';
import { downloadFile } from 'shared/utils/downloadFile';

function* getEcommerceOrdersSaga({
  payload
}: TAction<{
  token?: string;
  affiliate?: string;
}>) {
  try {
    const result: IEcommerceOrder[] = yield call(
      EcommerceService.getEcommerceOrders,
      payload
    );

    yield put(setEcommerceOrders(result));
  } catch (error) {
    yield put(actionFail());
    console.log('getOrders error', error);
  }
}

function* getEcommerceOrderSaga({ payload }: TAction<string>) {
  try {
    const result: IEcommerceOrder = yield call(
      EcommerceService.getEcommerceOrder,
      payload
    );
    yield put(setEcommerceCurrentOrder(result));
  } catch (error) {
    yield put(actionFail());
    console.log('getOrder error', error);
  }
}

function* getEcommerceSubscriptionsSaga({
  payload
}: TAction<{
  token?: string;
  affiliate?: string;
}>) {
  try {
    const result: IEcommerceSubscription[] = yield call(
      EcommerceService.getEcommerceSubscriptions,
      payload
    );

    yield put(setEcommerceSubscriptions(result));
  } catch (error) {
    yield put(actionFail());
    console.log('getOrders error', error);
  }
}

function* getEcommerceSubscriptionSaga({ payload }: TAction<string>) {
  try {
    const result: IEcommerceSubscription = yield call(
      EcommerceService.getEcommerceSubscription,
      payload
    );
    yield put(setEcommerceCurrentSubscription(result));
  } catch (error) {
    yield put(actionFail());
    console.log('getOrder error', error);
  }
}

function* getOrderReceiptSaga({
  payload
}: TAction<{ orderId: string; affiliate: string }>) {
  try {
    const result: string = yield call(
      EcommerceService.getOrderReceipt,
      payload
    );
    downloadFile(result, 'receipt.pdf', 'application/pdf');
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: i18n.t('ordersHistory.success', {
        defaultValue: 'Success',
        ns: I18N_NAMESPACE.ordersHistory
      }),
      message: i18n.t('ordersHistory.recipeDownloadedSuccessfully', {
        defaultValue: 'Receipt downloaded successfully',
        ns: I18N_NAMESPACE.ordersHistory
      })
    });
  } catch (error) {
    console.log('getOrderReceipt error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: i18n.t('ordersHistory.error', {
        defaultValue: 'Error',
        ns: I18N_NAMESPACE.ordersHistory
      }),
      message: error?.response?.data?.message
    });
  }
}

function* cancelSubscriptionSaga({
  payload
}: TAction<{
  subscriptionId: number;
  affiliate: string;
}>) {
  try {
    yield put(setLoadingActionKey('cancel'));
    const result: IEcommerceSubscription = yield call(
      EcommerceService.cancelSubscription,
      payload
    );
    yield put(setEcommerceCurrentSubscription(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: i18n.t('subscriptionsHistory.success', {
        defaultValue: 'Success',
        ns: I18N_NAMESPACE.subscriptionsHistory
      }),
      message: i18n.t('subscriptionsHistory.subscriptionCanceledSuccessfully', {
        defaultValue: 'Subscription canceled successfully',
        ns: I18N_NAMESPACE.subscriptionsHistory
      })
    });
  } catch (error) {
    yield put(actionFail());
    console.log('cancelSubscriptionSaga error', error);
  } finally {
    yield put(setLoadingActionKey(null));
  }
}

function* pauseSubscriptionSaga({
  payload
}: TAction<{
  subscriptionId: number;
  affiliate: string;
}>) {
  try {
    yield put(setLoadingActionKey('pause'));
    const result: IEcommerceSubscription = yield call(
      EcommerceService.pauseSubscription,
      payload
    );
    yield put(setEcommerceCurrentSubscription(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: i18n.t('subscriptionsHistory.success', {
        defaultValue: 'Success',
        ns: I18N_NAMESPACE.subscriptionsHistory
      }),
      message: i18n.t('subscriptionsHistory.subscriptionPausedSuccessfully', {
        defaultValue: 'Subscription paused successfully',
        ns: I18N_NAMESPACE.subscriptionsHistory
      })
    });
  } catch (error) {
    yield put(actionFail());
    console.log('pauseSubscriptionSaga error', error);
  } finally {
    yield put(setLoadingActionKey(null));
  }
}

function* resumeSubscriptionSaga({
  payload
}: TAction<{
  subscriptionId: number;
  affiliate: string;
}>) {
  try {
    yield put(setLoadingActionKey('resume'));
    const result: IEcommerceSubscription = yield call(
      EcommerceService.resumeSubscription,
      payload
    );
    yield put(setEcommerceCurrentSubscription(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: i18n.t('subscriptionsHistory.success', {
        defaultValue: 'Success',
        ns: I18N_NAMESPACE.subscriptionsHistory
      }),
      message: i18n.t('subscriptionsHistory.subscriptionResumedSuccessfully', {
        defaultValue: 'Subscription resumed successfully',
        ns: I18N_NAMESPACE.subscriptionsHistory
      })
    });
  } catch (error) {
    yield put(actionFail());
    console.log('resumeSubscriptionSaga error', error);
  } finally {
    yield put(setLoadingActionKey(null));
  }
}

function* setSubscriptionRenewalDateSaga({
  payload
}: TAction<{
  subscriptionId: number;
  affiliate: string;
  renewalDate: string;
}>) {
  try {
    yield put(setLoadingActionKey('adjust'));
    const result: IEcommerceSubscription = yield call(
      EcommerceService.setRenewalDate,
      payload
    );
    yield put(setEcommerceCurrentSubscription(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: i18n.t('subscriptionsHistory.success', {
        defaultValue: 'Success',
        ns: I18N_NAMESPACE.subscriptionsHistory
      }),
      message: i18n.t(
        'subscriptionsHistory.renewalSubscriptionUpdatedSuccessfully',
        {
          defaultValue: 'Subscription renewal date updated successfully',
          ns: I18N_NAMESPACE.subscriptionsHistory
        }
      )
    });
  } catch (error) {
    yield put(actionFail());
    console.log('setSubscriptionRenewalDateSaga error', error);
  } finally {
    yield put(setLoadingActionKey(null));
  }
}

function* Visits() {
  yield takeLatest(getEcommerceOrders.type, getEcommerceOrdersSaga);
  yield takeLatest(getEcommerceCurrentOrder.type, getEcommerceOrderSaga);
  yield takeLatest(
    getEcommerceSubscriptions.type,
    getEcommerceSubscriptionsSaga
  );
  yield takeLatest(
    getEcommerceCurrentSubscription.type,
    getEcommerceSubscriptionSaga
  );
  yield takeLatest(getOrderReceipt.type, getOrderReceiptSaga);
  yield takeLatest(cancelSubscription.type, cancelSubscriptionSaga);
  yield takeLatest(pauseSubscription.type, pauseSubscriptionSaga);
  yield takeLatest(resumeSubscription.type, resumeSubscriptionSaga);
  yield takeLatest(
    setSubscriptionRenewalDate.type,
    setSubscriptionRenewalDateSaga
  );
}

export default Visits;
